import * as Select from '@radix-ui/react-select';
import { Fragment } from 'react';
import type { RowComponentProps } from 'react-window';
import { HighlightedText, VirtualisedList } from '~/components/granular';
import { Divider } from '~/components/granular/divider';
import { SelectGroupHeading } from '~/components/granular/select/components/SelectGroupHeading';
import type { DropdownObjectItem } from '../../';
import { DropdownItem, useDropdownContext } from '../../';

export function DropdownTextItems({ className }: { className?: string }) {
  const { items, getLabel, getValue } = useDropdownContext();

  return items.map((item: DropdownObjectItem | string) => {
    const itemKey = `${getLabel(item).toLocaleLowerCase().replace(/\s/g, '-')}-${getValue(item)}`;
    return <TextItem className={className} item={item} key={itemKey} />;
  });
}

interface TextRowProps {
  items: Array<DropdownObjectItem | string>;
  className?: string;
}

// Must be a stable component: an inline rowComponent remounts every row on each render, and
// remounting the selected item makes Radix Select refocus it, stealing focus from the search input
function VirtualisedTextItemRow({ style, index, items, className }: RowComponentProps<TextRowProps>) {
  return <TextItem className={className} item={items[index]} style={style} />;
}

export function VirtualisedDropdownTextItems({
  className,
  itemSize = 36,
}: {
  className?: string;
  itemSize?: number;
  height?: number;
}) {
  const { items } = useDropdownContext();

  const itemCount = items.length ?? 0;
  const calculatedHeight = Math.min(itemCount * itemSize, itemCount ? 300 : 0);

  return (
    <VirtualisedList
      rowCount={itemCount}
      rowHeight={itemSize}
      style={{ height: calculatedHeight }}
      rowProps={{ items, className }}
      rowComponent={VirtualisedTextItemRow}
    />
  );
}

export function TextItem({
  className,
  item,
  style,
}: {
  className?: string;
  item: string | DropdownObjectItem;
  style?: React.CSSProperties;
}) {
  const { searchValue, isItemSelected, getLabel } = useDropdownContext();

  return (
    <DropdownItem className={className} isSelected={isItemSelected(item)} item={item} style={style}>
      <HighlightedText searchValue={searchValue} text={getLabel(item)} />
    </DropdownItem>
  );
}

/** Single-select groups using the same headings as dynamic filters. */
export function GroupedDropdownTextItems() {
  const { items, groupBy, getValue } = useDropdownContext();
  const groups = new Map<string, Array<DropdownObjectItem | string>>();
  for (const item of items) {
    const group = typeof item === 'string' || !groupBy ? '' : String(item[groupBy] ?? '');
    const groupItems = groups.get(group) ?? [];
    groupItems.push(item);
    groups.set(group, groupItems);
  }
  return (
    <div className="max-h-[300px] overflow-y-auto">
      {Array.from(groups, ([label, groupItems], index) => (
        <Fragment key={label}>
          <Select.Group>
            {label && (
              <Select.Label>
                <SelectGroupHeading>{label}</SelectGroupHeading>
              </Select.Label>
            )}
            {groupItems.map((item) => (
              <TextItem key={getValue(item)} item={item} className="h-8" />
            ))}
          </Select.Group>
          {index < groups.size - 1 && <Divider className="w-full" />}
        </Fragment>
      ))}
    </div>
  );
}
